// Die zwei Handgriffe, die jede Seite braucht: etwas laden, etwas tun - beides mit Fehlertext statt Absturz.

import { useCallback, useEffect, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from 'react'

import { fehlertext } from './api'
import { heuteIso } from './format'

/** sessionStorage kann fehlen oder werfen (privates Fenster, gesperrte Website-Daten) - dann eben ohne. */
function merkenLesen(schluessel: string): unknown {
  try {
    const roh = sessionStorage.getItem(schluessel)
    if (!roh) return undefined
    const { tag, wert } = JSON.parse(roh) as { tag?: string; wert?: unknown }
    // Nur für heute: wer das Fenster über Nacht offen lässt, beginnt morgen wieder mit den Vorgaben.
    return tag === heuteIso() ? wert : undefined
  } catch {
    return undefined
  }
}

function merkenSchreiben(schluessel: string, wert: unknown) {
  try {
    sessionStorage.setItem(schluessel, JSON.stringify({ tag: heuteIso(), wert }))
  } catch {
    // ohne Speicher geht nur das Merken verloren
  }
}

/**
 * Ein useState, der den Wechsel der Reiter übersteht: jeder Reiter ist eine eigene Route, die Seite wird
 * beim Wechsel abgebaut. Ohne das stand man beim Zurückkommen auf dem ersten Blatt mit heutigem Stichtag
 * und tippte leicht in die falsche Spalte (Frau Barth, 20.10.2026). Gilt je Browser-Tab und Tag.
 */
export function useGemerkt<T>(
  schluessel: string,
  start: T | (() => T),
  /** Gewinnt beim Öffnen über das Gemerkte, z. B. die Gruppe aus einem Link. */
  vorrang?: T | null,
): [T, Dispatch<SetStateAction<T>>] {
  const [wert, setWert] = useState<T>(() => {
    if (vorrang !== undefined && vorrang !== null) return vorrang
    const gemerkt = merkenLesen(schluessel)
    if (gemerkt !== undefined) return gemerkt as T
    return typeof start === 'function' ? (start as () => T)() : start
  })
  useEffect(() => merkenSchreiben(schluessel, wert), [schluessel, wert])
  return [wert, setWert]
}

/**
 * Wo man in einer Seite stand - senkrecht die Seite, waagerecht und senkrecht der Tabellenrahmen - beim
 * Verlassen merken und wiederherstellen, sobald `bereit` (die Tabelle ist da) zum ersten Mal wahr wird.
 * Den Rahmen auch senkrecht: in der Kladde scrollt die Tabelle in ihrem eigenen Rahmen (`max-height`), nicht
 * die Seite (04.11.2026); im Dispositionsblatt (`ganze-hoehe`) bleibt er oben, dort zählt die Seite.
 */
export function useScrollMerken(schluessel: string, rahmen: RefObject<HTMLElement | null>, bereit: boolean) {
  const wiederhergestellt = useRef(false)
  const stand = useRef<{ y: number; x: number; t: number } | null>(null)
  useEffect(() => {
    const gemerkt = merkenLesen(schluessel) as { y?: number; x?: number; t?: number } | undefined
    stand.current = gemerkt && typeof gemerkt.y === 'number' ? { y: gemerkt.y, x: gemerkt.x ?? 0, t: gemerkt.t ?? 0 } : null
    wiederhergestellt.current = false
  }, [schluessel])
  useEffect(() => {
    if (!bereit || wiederhergestellt.current) return
    wiederhergestellt.current = true
    const ziel = stand.current
    if (!ziel) return
    // Nach dem Rendern der Tabelle, sonst ist die Seite noch zu kurz für die alte Höhe.
    requestAnimationFrame(() => {
      if (rahmen.current) {
        rahmen.current.scrollLeft = ziel.x
        rahmen.current.scrollTop = ziel.t
      }
      window.scrollTo(0, ziel.y)
    })
  }, [bereit, rahmen])
  useEffect(() => {
    let angefordert = 0
    const sichern = () => {
      angefordert = 0
      // Erst nach dem Wiederherstellen schreiben - sonst überschreibt die frische, oben stehende Seite den Stand.
      if (wiederhergestellt.current)
        merkenSchreiben(schluessel, { y: window.scrollY, x: rahmen.current?.scrollLeft ?? 0, t: rahmen.current?.scrollTop ?? 0 })
    }
    const anstossen = () => {
      if (!angefordert) angefordert = requestAnimationFrame(sichern)
    }
    window.addEventListener('scroll', anstossen, { passive: true })
    // Der Rahmen meldet sein Scrollen nicht ans Fenster - mit capture auf dem Dokument hören.
    document.addEventListener('scroll', anstossen, { passive: true, capture: true })
    return () => {
      window.removeEventListener('scroll', anstossen)
      document.removeEventListener('scroll', anstossen, { capture: true })
      if (angefordert) cancelAnimationFrame(angefordert)
    }
  }, [schluessel, rahmen])
}

/**
 * Eine Aktion ausführen: Knöpfe sperren, alten Fehler und Erfolg löschen, Fehler als Text festhalten.
 * Was nach dem Erfolg passiert (neu laden, Meldung), gehört in die Aktion selbst.
 */
export function useAktion() {
  const [fehler, setFehler] = useState<string | null>(null)
  const [erfolg, setErfolg] = useState<string | null>(null)
  const [arbeitet, setArbeitet] = useState(false)

  const ausfuehren = useCallback(async <T,>(aktion: () => Promise<T>, erfolgstext?: string): Promise<T | undefined> => {
    setArbeitet(true)
    setFehler(null)
    setErfolg(null)
    try {
      const ergebnis = await aktion()
      if (erfolgstext) setErfolg(erfolgstext)
      return ergebnis
    } catch (f) {
      setFehler(fehlertext(f))
      return undefined
    } finally {
      setArbeitet(false)
    }
  }, [])

  return { fehler, erfolg, arbeitet, setFehler, setErfolg, ausfuehren }
}

/**
 * Daten laden, sobald sich die Abhängigkeiten ändern. Eine überholte Antwort (Gruppe zweimal schnell
 * gewechselt) wird verworfen, damit nie das alte Blatt über dem neuen erscheint.
 */
export function useLaden<T>(
  laden: () => Promise<T>,
  abhaengigkeiten: unknown[],
  optionen: { wenn?: boolean; onGeladen?: (daten: T) => void } = {},
) {
  const [daten, setDaten] = useState<T | null>(null)
  const [laedt, setLaedt] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const laufNummer = useRef(0)
  const { wenn = true, onGeladen } = optionen
  const onGeladenRef = useRef(onGeladen)
  onGeladenRef.current = onGeladen

  const neuLaden = useCallback(() => {
    if (!wenn) return
    const nummer = ++laufNummer.current
    setLaedt(true)
    setFehler(null)
    laden()
      .then((wert) => {
        if (nummer !== laufNummer.current) return
        setDaten(wert)
        onGeladenRef.current?.(wert)
      })
      .catch((f) => {
        if (nummer === laufNummer.current) setFehler(fehlertext(f))
      })
      .finally(() => {
        if (nummer === laufNummer.current) setLaedt(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wenn, ...abhaengigkeiten])

  useEffect(neuLaden, [neuLaden])

  return { daten, laedt, fehler, neuLaden, setDaten }
}
